/**
 * 刷新控制组件
 * 提供手动刷新按钮和自动刷新开关
 */
import React from 'react';
import { Button, Switch, Space, Tooltip, Badge } from 'antd';
import { ReloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { AutoRefreshReturn } from '../../hooks/useAutoRefresh';

export interface RefreshControlProps {
  refreshState: AutoRefreshReturn;
  showAutoRefresh?: boolean;
  showLastRefreshTime?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
}

export const RefreshControl: React.FC<RefreshControlProps> = ({
  refreshState,
  showAutoRefresh = true,
  showLastRefreshTime = true,
  size = 'middle',
  style
}) => {
  const {
    isRefreshing,
    lastRefreshTime,
    nextRefreshIn,
    manualRefresh,
    toggleAutoRefresh,
    isAutoRefreshEnabled,
    cooldownRemaining = 0,
    canManualRefresh = true
  } = refreshState;

  const formatLastRefreshTime = (time: Date | null) => {
    if (!time) return '从未刷新';

    const now = new Date();
    const diff = Math.floor((now.getTime() - time.getTime()) / 1000);

    if (diff < 60) return `${diff}秒前`;
    if (diff < 3600) return `${Math.floor(diff / 60)}分钟前`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}小时前`;
    return time.toLocaleString('zh-CN');
  };

  const getButtonText = () => {
    if (isRefreshing) return '刷新中';
    if (cooldownRemaining > 0) return `刷新 (${cooldownRemaining}s)`;
    return '刷新';
  };

  const getTooltipText = () => {
    if (cooldownRemaining > 0) {
      return `请等待 ${cooldownRemaining} 秒后再刷新`;
    }
    if (showLastRefreshTime) {
      return `上次刷新: ${formatLastRefreshTime(lastRefreshTime)}`;
    }
    return '刷新数据';
  };

  return (
    <Space size="middle" style={style}>
      <Tooltip title={getTooltipText()}>
        <Button
          type="default"
          size={size}
          icon={<ReloadOutlined spin={isRefreshing} />}
          onClick={manualRefresh}
          loading={isRefreshing}
          disabled={!canManualRefresh}
        >
          {getButtonText()}
        </Button>
      </Tooltip>

      {showAutoRefresh && (
        <Space
          size="small"
          style={{
            padding: '6px 10px',
            borderRadius: 999,
            background: 'rgba(210, 218, 224, 0.5)',
            border: '1px solid rgba(16, 27, 35, 0.08)',
            color: '#455763'
          }}
        >
          <Switch
            checked={isAutoRefreshEnabled}
            onChange={toggleAutoRefresh}
            checkedChildren="自动"
            unCheckedChildren="手动"
            size={size === 'small' ? 'small' : 'default'}
          />
          {isAutoRefreshEnabled && nextRefreshIn > 0 && (
            <Tooltip title="距离下次自动刷新">
              <Badge count={nextRefreshIn} showZero style={{ backgroundColor: '#b99258' }}>
                <ClockCircleOutlined style={{ fontSize: 16, color: '#b99258' }} />
              </Badge>
            </Tooltip>
          )}
        </Space>
      )}
    </Space>
  );
};

export interface DataChangeIndicatorProps {
  hasChanges: boolean;
  changeCount?: number;
  onViewChanges?: () => void;
  style?: React.CSSProperties;
}

export const DataChangeIndicator: React.FC<DataChangeIndicatorProps> = ({
  hasChanges,
  changeCount,
  onViewChanges,
  style
}) => {
  if (!hasChanges) return null;

  return (
    <div
      style={{
        padding: '10px 16px',
        background: 'rgba(124,140,255,0.12)',
        border: '1px solid rgba(124,140,255,0.28)',
        borderRadius: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: '#10171d',
        ...style
      }}
    >
      <Space>
        <Badge status="processing" />
        <span>
          {changeCount ? `检测到 ${changeCount} 条数据更新` : '数据已更新'}
        </span>
      </Space>
      {onViewChanges && (
        <Button type="link" size="small" onClick={onViewChanges} style={{ color: '#b99258' }}>
          查看更新
        </Button>
      )}
    </div>
  );
};

export interface RefreshIndicatorProps {
  isRefreshing: boolean;
  lastRefreshTime: Date | null;
  size?: 'small' | 'default';
}

export const RefreshIndicator: React.FC<RefreshIndicatorProps> = ({
  isRefreshing,
  lastRefreshTime,
  size = 'default'
}) => {
  const formatTime = (time: Date | null) => {
    if (!time) return '';
    return time.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <Space size="small" style={{ color: '#667986', fontSize: size === 'small' ? 12 : 14 }}>
      <ReloadOutlined spin={isRefreshing} />
      <span>
        {isRefreshing ? '刷新中...' : lastRefreshTime ? `更新于 ${formatTime(lastRefreshTime)}` : '等待刷新'}
      </span>
    </Space>
  );
};
